// src/pages/SuccessPayment.jsx
import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { CheckCircle, AlertTriangle, Loader2, Home, Share2, ExternalLink } from "lucide-react";
import { useRegionalExperience } from "../hooks/useRegionalExperience";

// Fire the GA4 `purchase` event once per Stripe session, from server-confirmed
// amount/currency. A per-session sessionStorage flag prevents a re-fire on
// refresh. Best-effort: tracking never blocks or surfaces to the donor.
function firePurchaseOnce({ sessionId, amount, currency, donationType }) {
  if (typeof window === "undefined" || !sessionId) return;
  const value = Number(amount) || 0;
  if (!value) return;
  const flagKey = `agm_purchase_fired_${sessionId}`;
  try {
    if (window.sessionStorage.getItem(flagKey)) return;
    window.sessionStorage.setItem(flagKey, "1");
  } catch {
    /* storage disabled — still fire once for this page load */
  }
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "purchase",
    transaction_id: sessionId,
    value,
    currency: currency || "USD",
    donation_type: donationType,
    items: [
      {
        item_id: "donation_general_seva",
        item_name: "AGM Seva",
        price: value,
        quantity: 1,
      },
    ],
  });
}

// Stripe redirects away before the amount is known client-side, so confirm the
// paid amount/currency server-side (verify-session) and fire the purchase from
// that authoritative data. No-ops without a session_id (e.g. Razorpay
// subscription, which fires inline in the form) — preventing any double-count.
async function fireStripePurchase(sessionId, donationType) {
  if (typeof window === "undefined" || !sessionId) return;
  try {
    const res = await fetch(
      `${import.meta.env.VITE_APP_BASE_URL}/verify-session?session_id=${sessionId}`
    );
    const data = await res.json();
    if (data.success && data.payment_status === "paid") {
      firePurchaseOnce({
        sessionId,
        amount: data.amount,
        currency: data.currency,
        donationType,
      });
    }
  } catch {
    /* tracking is best-effort — never surface to the donor */
  }
}

function SuccessPayment() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const provider = searchParams.get("provider");
  const tokenParam = searchParams.get("token");
  const receiptParam = searchParams.get("receipt");
  const [status, setStatus] = useState("loading");
  const [receiptNumber, setReceiptNumber] = useState(receiptParam || null);
  const [receiptToken, setReceiptToken] = useState(tokenParam || null);
  const [monthlyIntentSent, setMonthlyIntentSent] = useState(false);
  const [monthlyEmail, setMonthlyEmail] = useState("");
  const [monthlyEmailError, setMonthlyEmailError] = useState("");
  const [monthlyIntentError, setMonthlyIntentError] = useState("");
  const navigate = useNavigate();
  const { region, regionKey } = useRegionalExperience();

  useEffect(() => {
    // Subscription: status is already settled server-side (the Stripe/Razorpay
    // subscription webhook records + receipts the first charge), so we just show
    // success. Tracking still fires below.
    if (searchParams.get('type') === 'subscription') {
      setStatus('success');
      // Stripe recurring carries a session_id (fire from server-confirmed data);
      // Razorpay recurring has none + already fired inline → no-op here.
      fireStripePurchase(sessionId, 'recurring');
      return;
    }
    // Razorpay: already HMAC-verified + recorded server-side before redirect.
    if (provider === "razorpay" || tokenParam || receiptParam) {
      setStatus("success");
      return;
    }

    async function verifyPayment() {
      try {
        const res = await fetch(
          `${import.meta.env.VITE_APP_BASE_URL}/verify-session?session_id=${sessionId}`
        );
        const data = await res.json();

        if (data.success && data.payment_status === "paid") {
          if (data.receiptNumber) setReceiptNumber(data.receiptNumber);
          if (data.receiptToken) setReceiptToken(data.receiptToken);
          setStatus("success");
          // One-time Stripe: fire from this same server-confirmed response.
          firePurchaseOnce({
            sessionId,
            amount: data.amount,
            currency: data.currency,
            donationType: "one_time",
          });
        } else {
          setStatus("invalid");
        }
      } catch (error) {
        console.error("Verification error:", error);
        setStatus("error");
      }
    }

    if (sessionId) {
      verifyPayment();
    } else {
      setStatus("invalid");
    }
  }, [sessionId, provider, tokenParam, receiptParam]);

  const handleGoHome = () => navigate("/");

  const handleMonthlyIntent = async () => {
    // Validate email inline before posting
    const trimmed = monthlyEmail.trim();
    if (!trimmed || !trimmed.includes("@") || !trimmed.includes(".")) {
      setMonthlyEmailError("Please enter a valid email address.");
      return;
    }
    setMonthlyEmailError("");
    setMonthlyIntentError("");

    const apiBase = import.meta.env.VITE_APP_BASE_URL?.replace(/\/$/, "");
    if (!apiBase) {
      setMonthlyIntentSent(true);
      return;
    }
    try {
      const res = await fetch(`${apiBase}/donations/monthly-interest`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: trimmed,
          amount: 0,
          region: regionKey || "IN",
        }),
      });
      if (res.ok) {
        setMonthlyIntentSent(true);
      } else {
        setMonthlyIntentError("Couldn't save that just now — please try again.");
      }
    } catch {
      setMonthlyIntentError("Couldn't save that just now — please try again.");
    }
  };

  const handleShare = async () => {
    const shareData = {
      title: "I supported AGM seva",
      text: "I contributed to AvdheshanandG Mission — supporting healthcare, education and dignity through seva.",
      url: window.location.origin,
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch {
        /* user dismissed */
      }
    } else {
      try {
        await navigator.clipboard.writeText(shareData.url);
        alert("Link copied.");
      } catch {
        /* clipboard unavailable */
      }
    }
  };

  if (searchParams.get('type') === 'subscription') {
    return (
      <div className="min-h-screen bg-[#f7faf9] flex items-center justify-center py-12 px-4">
        <div className="w-full max-w-lg space-y-6">
          <div className="rounded-xl border border-[#dfe8e6] bg-white px-6 py-6 text-center space-y-4">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#edf4f2]">
              <CheckCircle className="h-7 w-7 text-[#9d1f1c]" />
            </div>
            <h1 className="text-2xl font-semibold text-[#14201f]">Your monthly seva is set up 🙏</h1>
            <p className="text-sm leading-6 text-[#52615f]">
              Thank you. A confirmation with a link to manage or stop it anytime has been emailed to you.
            </p>
          </div>
          <div className="flex flex-col items-center gap-3 text-center">
            <a
              href="/programs"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-[#14201f] hover:underline"
            >
              Explore our work
            </a>
            <button
              type="button"
              onClick={handleGoHome}
              className="text-xs text-[#52615f] hover:underline"
            >
              Return home
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (status === "loading") {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-blue-100">
              <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
            </div>
            <CardTitle className="text-xl">Verifying Payment</CardTitle>
            <CardDescription>Please wait while we confirm your payment...</CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <div className="space-y-4">
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-600 rounded-full animate-pulse"
                  style={{ width: "60%" }}
                />
              </div>
              <p className="text-sm text-gray-600">This may take a few moments</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === "success") {
    const blessingLine =
      region?.blessing ||
      "Pranam — your contribution is received with gratitude and will support seva.";

    return (
      <div className="min-h-screen bg-[#f7faf9] flex items-center justify-center py-12 px-4">
        <div className="w-full max-w-lg space-y-6">
          {/* Blessing */}
          <div className="rounded-xl border border-[#dfe8e6] bg-white px-6 py-5 text-center">
            <p className="text-base leading-7 text-[#52615f] italic">{blessingLine}</p>
          </div>

          {/* Confirmation */}
          <div className="rounded-xl border border-[#dfe8e6] bg-white px-6 py-6 text-center space-y-4">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#edf4f2]">
              <CheckCircle className="h-7 w-7 text-[#9d1f1c]" />
            </div>
            <h1 className="text-2xl font-semibold text-[#14201f]">Your seva is received.</h1>
            <p className="text-sm leading-6 text-[#52615f]">
              Your contribution has been recorded. Thank you for supporting AGM's mission.
            </p>

            {receiptNumber && (
              <p className="text-sm text-[#52615f]">
                Receipt No:{" "}
                <span className="font-semibold text-[#14201f]">{receiptNumber}</span>
              </p>
            )}

            {receiptToken ? (
              <a
                href={`${import.meta.env.VITE_APP_BASE_URL}/donations/receipt?token=${receiptToken}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#9d1f1c] px-5 py-3 text-sm font-semibold text-white hover:bg-[#7f1916]"
              >
                Download receipt (PDF)
              </a>
            ) : (
              <p className="text-xs text-[#52615f]">
                Your acknowledgement has been emailed to you.
              </p>
            )}
          </div>

          {/* Monthly seva intent */}
          <div className="rounded-xl border border-[#dfe8e6] bg-white px-6 py-5 text-center space-y-3">
            {monthlyIntentSent ? (
              <p className="text-sm text-[#52615f]">
                🙏 We'll be in touch about monthly seva. Thank you.
              </p>
            ) : (
              <>
                <p className="text-sm leading-6 text-[#14201f] font-medium">
                  Would you consider a monthly seva?
                </p>
                <p className="text-xs text-[#52615f]">
                  Share your interest — our team will follow up. No commitment today.
                </p>
                <div className="space-y-1 text-left">
                  <label
                    htmlFor="monthly-email"
                    className="block text-xs font-medium text-[#52615f]"
                  >
                    Email for monthly-seva updates
                  </label>
                  <input
                    id="monthly-email"
                    type="email"
                    value={monthlyEmail}
                    onChange={(e) => {
                      setMonthlyEmail(e.target.value);
                      if (monthlyEmailError) setMonthlyEmailError("");
                      if (monthlyIntentError) setMonthlyIntentError("");
                    }}
                    placeholder="your@email.com"
                    className="w-full rounded-md border border-[#dfe8e6] px-3 py-2 text-sm text-[#14201f] placeholder-[#a0aaa8] focus:border-[#9d1f1c] focus:outline-none focus:ring-1 focus:ring-[#9d1f1c]"
                  />
                  {monthlyEmailError && (
                    <p className="text-xs text-[#9d1f1c]">{monthlyEmailError}</p>
                  )}
                </div>
                {monthlyIntentError && (
                  <p className="text-xs text-[#52615f]">{monthlyIntentError}</p>
                )}
                <button
                  type="button"
                  onClick={handleMonthlyIntent}
                  className="inline-flex items-center justify-center rounded-md border border-[#9d1f1c] px-5 py-2.5 text-sm font-semibold text-[#9d1f1c] hover:bg-[#fff5f2]"
                >
                  Yes, I am interested
                </button>
              </>
            )}
          </div>

          {/* Share */}
          <div className="rounded-xl border border-[#dfe8e6] bg-white px-6 py-4 text-center">
            <button
              type="button"
              onClick={handleShare}
              className="inline-flex items-center gap-2 text-sm font-medium text-[#9d1f1c] hover:underline"
            >
              <Share2 className="h-4 w-4" />
              Share this seva
            </button>
          </div>

          {/* Explore + Return home */}
          <div className="flex flex-col items-center gap-3 text-center">
            <a
              href="/programs"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-[#14201f] hover:underline"
            >
              <ExternalLink className="h-4 w-4" />
              Explore our work
            </a>
            <button
              type="button"
              onClick={handleGoHome}
              className="text-xs text-[#52615f] hover:underline"
            >
              Return home
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (status === "invalid" || status === "error") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
              <AlertTriangle className="h-8 w-8 text-red-600" />
            </div>
            <CardTitle className="text-2xl text-red-800">Payment Issue</CardTitle>
            <CardDescription className="text-lg">⚠️ Invalid or incomplete session</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="text-center space-y-2">
              <p className="text-gray-600">
                We couldn't verify your payment session. This might be due to:
              </p>
              <ul className="text-sm text-gray-500 space-y-1 text-left">
                <li>• Expired payment session</li>
                <li>• Invalid payment link</li>
                <li>• Network connectivity issues</li>
              </ul>
            </div>
            <div className="space-y-3">
              <Button onClick={handleGoHome} className="w-full" size="lg">
                <Home className="mr-2 h-4 w-4" />
                Go to Homepage
              </Button>
              <Button variant="outline" onClick={() => window.location.reload()} className="w-full">
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return null;
}

export default SuccessPayment;
